import { useMutation } from "@tanstack/react-query";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { useModals } from "react-easy-modals";
import * as yup from "yup";
import { colors, spacings } from "@/assets/themes";
import AddressModal from "@/components/address-modal";
import { useAuth } from "@/modules/hooks/use-auth";
import useToast from "@/modules/hooks/use-toast";
import {
  addressValidation,
  firstNameValidation,
  lastNameValidation,
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  PASSWORD_MINIMAL_LENGTH,
  setAddressInitialValues,
} from "@/utils/index";
import polyglot from "@/utils/polyglot";
import Block from "../block";
import Button from "../button";
import Checkbox from "../checkbox";
import ConditionnalWrapper from "../conditionnal-wrapper";
import FormControl from "../form-control";
import Icon from "../icon";
import Input from "../input";
import Modal from "../modal/index";
import PasswordChecker from "../password/password-checker";
import PostalCodeInput from "../postal-code-input";
import { Body16 } from "../text";
import SignUpTerms from "./signup-terms";

// ISPRO ONLY
const getConditionalAddressValidation = (addressValidation) => {
  const conditionalValidation = {};

  Object.keys(addressValidation).forEach((fieldName) => {
    const originalValidation = addressValidation[fieldName];

    conditionalValidation[fieldName] = yup.string().nullable().when("user.is_pro", {
      is: true,
      then: originalValidation,
      otherwise: yup.string().nullable().notRequired(),
    });
  });

  return conditionalValidation;
};

const signUpSchema = yup.object().shape(
  {
    user: yup.object().shape({
      email: yup
        .string()
        .email(polyglot.t("common.errors.email_invalid"))
        .required(polyglot.t("common.errors.field_required")),
      ...lastNameValidation,
      ...firstNameValidation,
      is_jobber: yup.boolean(),
      is_pro: yup.boolean(),
      kind: yup.string(),
      company_name: yup.string().when("is_pro", {
        is: true,
        then: yup.string().required(polyglot.t("common.errors.field_required")),
      }),
      password: yup
        .string()
        .min(
          PASSWORD_MINIMAL_LENGTH,
          polyglot.t("common.errors.password_too_short_plural", {
            count: PASSWORD_MINIMAL_LENGTH,
          })
        )
        .required(polyglot.t("common.errors.field_required")),
    }),
    alert_setting: yup.object().shape({
      coreg: yup.boolean(),
      optin: yup.boolean(),
    }),
    ...getConditionalAddressValidation(addressValidation),
  },
  [
    ["place_id", "address_id"],
    ["address_name", "city_id"],
  ]
);

function SignUp({
  isOpen,
  close,
  isPro = false,
  values = {},
  params,
  redirectionPath,
  user,
  noModal = false,
  resolve,
  preventRedirect = false,
}) {
  const modals = useModals();
  const { signUp, redirect } = useAuth();
  const [addressModalIsOpen, setAddressModalIsOpen] = useState(false);

  const signUpMutation = useMutation({
    mutationFn: (values) => signUp(values),
    onSuccess: (response) => {
      if (!preventRedirect) {
        redirect(redirectionPath);
      } else if (resolve) {
        // resolve is used to resolve promise from main modal
        resolve(response);
        // close this current modal after resolving the promise (and close the main modal)
        close(response);
      } else {
        close(response);
      }
    },
  });

  const onSubmit = async (values) => {
    signUpMutation.mutate(values);
  };

  const handlePreventArobase = (event, { setFieldValue }) => {
    setFieldValue(event.currentTarget.name, event.currentTarget.value.replace("@", ""));
  };

  return (
    <ConditionnalWrapper
      condition={!noModal}
      wrapper={(children) => (
        <Modal.Small
          animate={modals.stack.length === 1}
          fullScreenOnMobile
          isOpen={isOpen}
          onClose={() => modals.closeAll()}
        >
          {children}
        </Modal.Small>
      )}
    >
      <Formik
        initialValues={{
          user: {
            email: values?.email || user?.email || "",
            first_name: values?.first_name || "",
            last_name: values?.last_name || "",
            password: "",
            company_name: "",
            is_pro: isPro,
            kind: isPro ? "company" : "individual",
          },
          alert_setting: { coreg: true, optin: true },
          ...setAddressInitialValues(),
          ...params,
        }}
        onSubmit={onSubmit}
        validateOnChange
        validateOnMount
        validationSchema={signUpSchema}
      >
        {({
          values: formValues,
          errors,
          touched,
          handleChange,
          handleBlur,
          isValid,
          setFieldValue,
        }) => (
          <div>
            <Modal.Item.Header
              onBack={
                // eslint-disable-next-line no-nested-ternary
                noModal ? undefined : modals.stack.length === 1 ? undefined : () => close()
              }
              onClose={
                // eslint-disable-next-line no-nested-ternary
                noModal
                  ? undefined
                  : modals.stack.length === 1
                    ? () => close()
                    : () => modals.closeAll()
              }
              title={isPro ? polyglot.t("common.signup_title") : polyglot.t("signup.finish_signup")}
            />
            <Modal.Item.Wrapper>
              <Form>
                {/* Email */}
                <FormControl
                  error={touched.user?.email && errors.user?.email ? errors.user.email : undefined}
                  label={polyglot.t("common.email")}
                >
                  <Input
                    autoComplete="email"
                    disabled={!!user?.email}
                    name="user.email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder={polyglot.t("common.email")}
                    type="email"
                    value={formValues.user.email}
                  />
                </FormControl>

                {/* First Name */}
                <FormControl
                  error={
                    touched.user?.first_name && errors.user?.first_name
                      ? errors.user.first_name
                      : undefined
                  }
                  label={polyglot.t("common.first_name")}
                >
                  <Input
                    maxLength={MAX_FIRST_NAME_LENGTH}
                    name="user.first_name"
                    onBlur={handleBlur}
                    onChange={(event) => handlePreventArobase(event, { setFieldValue })}
                    placeholder={polyglot.t("common.first_name")}
                    type="text"
                    value={formValues.user.first_name}
                  />
                </FormControl>

                {/* Last Name */}
                <FormControl
                  error={
                    touched.user?.last_name && errors.user?.last_name
                      ? errors.user.last_name
                      : undefined
                  }
                  label={polyglot.t("common.last_name")}
                >
                  <Input
                    maxLength={MAX_LAST_NAME_LENGTH}
                    name="user.last_name"
                    onBlur={handleBlur}
                    onChange={(event) => handlePreventArobase(event, { setFieldValue })}
                    placeholder={polyglot.t("common.last_name")}
                    type="text"
                    value={formValues.user.last_name}
                  />
                </FormControl>

                {/* Zipcode for Jobbers */}
                {formValues.user.is_jobber && (
                  <FormControl
                    error={touched.zipcode && errors.zipcode ? errors.zipcode : undefined}
                    label={polyglot.t("common.zipcode")}
                  >
                    <PostalCodeInput
                      name="joblist_address.city_id"
                      placeholder={polyglot.t("common.zipcode")}
                    />
                  </FormControl>
                )}

                {/* Company Name for Pros */}
                {formValues.user.is_pro && (
                  <>
                    <FormControl
                      error={
                        touched.user?.company_name && errors.user?.company_name
                          ? errors.user.company_name
                          : undefined
                      }
                      label={polyglot.t("common.company_name")}
                    >
                      <Input
                        name="user.company_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder={polyglot.t("common.company_name")}
                        type="text"
                        value={formValues.user.company_name}
                      />
                    </FormControl>
                    <FormControl label={polyglot.t("common.address")}>
                      <Input
                        LeftComponent={() => (
                          <Icon.Large color={colors.mutedLight} name="map-marker" />
                        )}
                        onClick={() => setAddressModalIsOpen(true)}
                        placeholder={polyglot.t("common.placeholder.address")}
                        RightComponent={() => <Icon.Medium name="dropdown" />}
                        value={formValues.address}
                      />
                      <AddressModal
                        isOpen={addressModalIsOpen}
                        onClose={() => setAddressModalIsOpen(false)}
                      />
                    </FormControl>
                  </>
                )}

                {/* Password */}
                <Block display="flex" flexDirection="column" gap={spacings.s}>
                  <FormControl
                    error={
                      touched.user?.password && errors.user?.password
                        ? errors.user.password
                        : undefined
                    }
                    label={polyglot.t("common.password")}
                  >
                    <Input
                      autoComplete="new-password"
                      name="user.password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder={polyglot.t("common.password")}
                      type="password"
                      value={formValues.user.password}
                    />
                    <Block marginTop={spacings.s}>
                      <PasswordChecker value={formValues.user.password} />
                    </Block>
                  </FormControl>
                </Block>

                {/* Alert Settings */}
                <Block marginBottom={spacings.m}>
                  <Field name="alert_setting.optin" type="checkbox">
                    {({ field }) => (
                      <FormControl>
                        <Checkbox
                          {...field}
                          onChange={(event) => {
                            field.onChange(event);
                            setFieldValue("alert_setting.coreg", event.target.checked);
                          }}
                        >
                          <Body16 color={colors.muted}>{polyglot.t("optin.main")}</Body16>
                        </Checkbox>
                      </FormControl>
                    )}
                  </Field>
                  <input name="alert_setting.coreg" type="hidden" />
                </Block>

                <Button.Large
                  block
                  disabled={!isValid}
                  isLoading={signUpMutation.isPending || signUpMutation.isSuccess}
                  type="submit"
                >
                  {polyglot.t("common.signup")}
                </Button.Large>
                <SignUpTerms />
              </Form>
            </Modal.Item.Wrapper>
          </div>
        )}
      </Formik>
    </ConditionnalWrapper>
  );
}

export default SignUp;
